import { BlockMetadata, BlockType } from '../types/builder';

export const BLOCK_REGISTRY: Record<BlockType, BlockMetadata> = {
  HEADER: {
    type: 'HEADER',
    name: 'Header',
    description: 'Shop header with logo, name, and search',
    icon: '🏪',
    category: 'header',
    requiredPlan: 'BASIC',
    preview: '/previews/header.png',
    defaultConfig: {
      shopName: 'My Shop',
      logo: '',
      showSearch: true,
      searchPlaceholder: 'Search products...',
      bgColor: '#FFFFFF',
      textColor: '#1D1D1F',
      height: '80px',
      logoHeight: '60px',
      sticky: true
    }
  },

  ANNOUNCEMENT_BAR: {
    type: 'ANNOUNCEMENT_BAR',
    name: 'Announcement Bar',
    description: 'Display important messages at the top',
    icon: '📢',
    category: 'header',
    requiredPlan: 'BASIC',
    preview: '/previews/announcement-bar.png',
    defaultConfig: {
      text: '🎉 Free delivery on orders above ₹500',
      bgColor: '#34C759',
      textColor: '#FFFFFF',
      link: '',
      dismissible: true
    }
  },

  HERO_BANNER: {
    type: 'HERO_BANNER',
    name: 'Hero Banner',
    description: 'Large banner with call-to-action',
    icon: '🎭',
    category: 'hero',
    requiredPlan: 'BASIC',
    preview: '/previews/hero-banner.png',
    defaultConfig: {
      title: 'Welcome to Our Shop',
      subtitle: 'Quality products delivered to your door',
      bgImage: '',
      bgVideo: '',
      overlay: true,
      overlayOpacity: 0.5,
      ctaText: 'Shop Now',
      ctaLink: '/products',
      ctaStyle: 'primary',
      alignment: 'center',
      height: 'large',
      displayLocation: false,
      displayConnect: false
    }
  },

  PRODUCT_GRID: {
    type: 'PRODUCT_GRID',
    name: 'Product Grid',
    description: 'Display products in a grid layout',
    icon: '🛍️',
    category: 'products',
    requiredPlan: 'BASIC',
    preview: '/previews/product-grid.png',
    defaultConfig: {
      title: 'Our Products',
      columns: 4,
      showPrice: true,
      showCart: true,
      showQuickView: false,
      filterBy: [],
      sortBy: 'name',
      limit: 12,
      layout: 'grid'
    }
  },

  TESTIMONIALS: {
    type: 'TESTIMONIALS',
    name: 'Testimonials',
    description: 'Customer reviews and testimonials',
    icon: '⭐',
    category: 'testimonials',
    requiredPlan: 'BASIC',
    preview: '/previews/testimonials.png',
    defaultConfig: {
      title: 'What Our Customers Say',
      items: [],
      layout: 'carousel',
      autoPlay: true,
      interval: 5000,
      showRating: true
    }
  },

  FAQ: {
    type: 'FAQ',
    name: 'FAQ Section',
    description: 'Frequently asked questions',
    icon: '❓',
    category: 'content',
    requiredPlan: 'BASIC',
    preview: '/previews/faq.png',
    defaultConfig: {
      title: 'Frequently Asked Questions',
      style: 'accordion',
      items: [
        {
          question: 'What are your delivery hours?',
          answer: 'We deliver from 9 AM to 9 PM every day.',
          expanded: false
        },
        {
          question: 'Do you accept online payments?',
          answer: 'Yes, we accept all major payment methods.',
          expanded: false
        }
      ]
    }
  },

  CONTACT_FORM: {
    type: 'CONTACT_FORM',
    name: 'Contact Form',
    description: 'Get in touch form',
    icon: '📧',
    category: 'forms',
    requiredPlan: 'BASIC',
    preview: '/previews/contact-form.png',
    defaultConfig: {
      title: 'Get In Touch',
      showMap: true,
      mapLocation: { lat: 0, lng: 0 },
      fields: [
        { name: 'name', type: 'text', required: true, placeholder: 'Your Name' },
        { name: 'email', type: 'email', required: true, placeholder: 'Your Email' },
        { name: 'phone', type: 'phone', required: false, placeholder: 'Your Phone' },
        { name: 'message', type: 'textarea', required: true, placeholder: 'Your Message' }
      ],
      submitText: 'Send Message',
      successMessage: 'Thank you! We will get back to you soon.'
    }
  },

  IMAGE_GALLERY: {
    type: 'IMAGE_GALLERY',
    name: 'Image Gallery',
    description: 'Showcase up to 20 images in a beautiful gallery',
    icon: '🖼️',
    category: 'media',
    requiredPlan: 'BASIC',
    preview: '/previews/image-gallery.png',
    defaultConfig: {
      title: 'Image Gallery',
      subtitle: 'Browse our collection',
      columns: 4,
      gap: 16,
      lightbox: true,
      showCaptions: true,
      images: []
    }
  },

  VIDEO_SECTION: {
    type: 'VIDEO_SECTION',
    name: 'Video Section',
    description: 'Embed videos',
    icon: '🎥',
    category: 'media',
    requiredPlan: 'BASIC',
    preview: '/previews/video-section.png',
    defaultConfig: {
      title: 'Watch Video',
      videoUrl: '',
      thumbnail: '',
      autoPlay: false,
      controls: true,
      loop: false,
      muted: false
    }
  },

  COUNTDOWN_TIMER: {
    type: 'COUNTDOWN_TIMER',
    name: 'Countdown Timer',
    description: 'Create urgency with countdown',
    icon: '⏰',
    category: 'advanced',
    requiredPlan: 'BASIC',
    preview: '/previews/countdown.png',
    defaultConfig: {
      title: 'Limited Time Offer',
      endDate: '',
      showDays: true,
      showHours: true,
      showMinutes: true,
      showSeconds: true,
      bgColor: '#FF3B30',
      textColor: '#FFFFFF'
    }
  },

  CUSTOM_HTML: {
    type: 'CUSTOM_HTML',
    name: 'Custom HTML',
    description: 'Add custom HTML code from AI tools or manual input',
    icon: '🔧',
    category: 'advanced',
    requiredPlan: 'BASIC',
    preview: '/previews/custom-html.png',
    defaultConfig: {
      html: '',
      version: 1,
      source: 'manual',
      lastValidated: null
    }
  },

  TEXT_SECTION: {
    type: 'TEXT_SECTION',
    name: 'Text Section',
    description: 'Rich text content',
    icon: '📝',
    category: 'content',
    requiredPlan: 'BASIC',
    preview: '/previews/text-section.png',
    defaultConfig: {
      title: 'About Us',
      content: 'Add your content here...',
      alignment: 'left',
      bgColor: '#FFFFFF',
      textColor: '#1D1D1F'
    }
  },

  IMAGE_TEXT: {
    type: 'IMAGE_TEXT',
    name: 'Image + Text',
    description: 'Image with text side by side',
    icon: '🖼️📝',
    category: 'content',
    requiredPlan: 'BASIC',
    preview: '/previews/image-text.png',
    defaultConfig: {
      title: 'Our Story',
      content: 'Tell your story...',
      image: '',
      imagePosition: 'left',
      imageWidth: '50%'
    }
  },

  FEATURES: {
    type: 'FEATURES',
    name: 'Features Grid',
    description: 'Highlight key features',
    icon: '✨',
    category: 'content',
    requiredPlan: 'BASIC',
    preview: '/previews/features.png',
    defaultConfig: {
      title: 'Why Choose Us',
      columns: 3,
      features: [
        { icon: '🚚', title: 'Fast Delivery', description: 'Quick and reliable delivery' },
        { icon: '💰', title: 'Best Prices', description: 'Competitive pricing' },
        { icon: '🛡️', title: 'Quality Assured', description: 'Top quality products' }
      ]
    }
  },

  PRICING: {
    type: 'PRICING',
    name: 'Pricing Table',
    description: 'Display pricing plans',
    icon: '💵',
    category: 'content',
    requiredPlan: 'BASIC',
    preview: '/previews/pricing.png',
    defaultConfig: {
      title: 'Our Plans',
      plans: []
    }
  },

  NEWSLETTER: {
    type: 'NEWSLETTER',
    name: 'Newsletter Signup',
    description: 'Email subscription form',
    icon: '📬',
    category: 'forms',
    requiredPlan: 'BASIC',
    preview: '/previews/newsletter.png',
    defaultConfig: {
      title: 'Subscribe to Our Newsletter',
      subtitle: 'Get updates and special offers',
      buttonText: 'Subscribe',
      bgColor: '#34C759',
      textColor: '#FFFFFF'
    }
  },

  SOCIAL_FEED: {
    type: 'SOCIAL_FEED',
    name: 'Social Media Feed',
    description: 'Display social media posts and feeds',
    icon: '📱',
    category: 'media',
    requiredPlan: 'BASIC',
    preview: '/previews/social-feed.png',
    defaultConfig: {
      title: 'Follow Us On Social Media',
      subtitle: 'Stay connected with our latest updates',
      feeds: [
        {
          id: '1',
          platform: 'instagram',
          accountName: '',
          accountUrl: '',
          feedUrl: '',
          caption: 'Follow us on Instagram',
          showPreview: true,
          enabled: true
        }
      ],
      columns: 3,
      showCaptions: true,
      bgColor: '#FFFFFF',
      textColor: '#1D1D1F'
    }
  },

  MAP: {
    type: 'MAP',
    name: 'Location Map',
    description: 'Display your location',
    icon: '🗺️',
    category: 'content',
    requiredPlan: 'BASIC',
    preview: '/previews/map.png',
    defaultConfig: {
      title: 'Find Us',
      latitude: 0,
      longitude: 0,
      zoom: 15,
      showMarker: true
    }
  }
};

export const getBlockMetadata = (type: BlockType): BlockMetadata => {
  return BLOCK_REGISTRY[type];
};

export const getBlocksByCategory = (category: string): BlockMetadata[] => {
  return Object.values(BLOCK_REGISTRY).filter(b => b.category === category);
};

export const getBlocksByPlan = (plan: 'BASIC' | 'PRO' | 'PREMIUM'): BlockMetadata[] => {
  const planHierarchy = { BASIC: 1, PRO: 2, PREMIUM: 3 };
  const userLevel = planHierarchy[plan];

  return Object.values(BLOCK_REGISTRY).filter(b => {
    const blockLevel = planHierarchy[b.requiredPlan];
    return blockLevel <= userLevel;
  });
};

